import { Editor } from "@tiptap/react";
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from "react";

import { GetJourneyForMessagesQuery } from "@typing/Generated";
import { UploadWithFile } from "@typing/Models";

type Journey = GetJourneyForMessagesQuery["journey"];

type SharedProps = {
  editor: Editor | null;
  editorRefresher?: {
    toggle: () => void;
    value: boolean;
  };
  // It's a little weird to ask for both the journey and the journeyId, but they're supplied to this
  // context in separate circumstances. The journey is used by the message template selector for
  // filling in the variables with journey data, while the journeyId is used for the in-app links
  // selector to both determine whether that selector is allowed to be shown and also make the API
  // requests necessary to generate the lists of links.
  journey?: Journey;
  journeyId?: string;
  setTemplateId?: Dispatch<SetStateAction<string | null>>;
};

export const RichTextareaContext = createContext<
  SharedProps & {
    insertContent: string | null;
    lastAnchorPoint: number | null;
    overwriteContent: string | null;
    setInsertContent: Dispatch<SetStateAction<string | null>>;
    setLastAnchorPoint: Dispatch<SetStateAction<number | null>>;
    setOverwriteContent: Dispatch<SetStateAction<string | null>>;
    setUploads: Dispatch<SetStateAction<UploadWithFile[]>>;
    setUploadsInProgress: Dispatch<SetStateAction<File[]>>;
    setWarning: Dispatch<SetStateAction<string | null>>;
    uploads: UploadWithFile[];
    uploadsInProgress: File[];
    warning: string | null;
  }
>({
  editor: null,
  insertContent: null,
  lastAnchorPoint: null,
  overwriteContent: null,
  setInsertContent: () => {},
  setLastAnchorPoint: () => {},
  setOverwriteContent: () => {},
  setUploads: () => {},
  setUploadsInProgress: () => {},
  setWarning: () => {},
  uploads: [],
  uploadsInProgress: [],
  warning: null
});

type Props = PropsWithChildren<
  SharedProps & {
    onUploadsChange?: (uploads: UploadWithFile[]) => void;
    uploadsWatch?: string[];
  }
>;

const RichTextareaProvider = ({ children, onUploadsChange, uploadsWatch, ...passAlong }: Props) => {
  const [insertContent, setInsertContent] = useState<string | null>(null);
  const [lastAnchorPoint, setLastAnchorPoint] = useState<number | null>(null);
  const [overwriteContent, setOverwriteContent] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadWithFile[]>([]);
  const [uploadsInProgress, setUploadsInProgress] = useState<File[]>([]);
  const [warning, setWarning] = useState<string | null>(null);

  // by allowing callers to pass a callback to be notified when the uploads change, we can make the
  // rich textarea agnostic about the form functionality of what happens with the uploads
  useEffect(() => {
    onUploadsChange && onUploadsChange(uploads);
  }, [onUploadsChange, uploads]);

  // the form can send a watch value for the field that the uploads are associated with, so we can
  // clear these values when that form field is emptied
  useEffect(() => {
    if (uploadsWatch && uploadsWatch.length === 0 && (uploads.length > 0 || uploadsInProgress.length > 0)) {
      setUploads([]);
      setUploadsInProgress([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadsWatch]);

  const value = {
    insertContent,
    lastAnchorPoint,
    overwriteContent,
    setInsertContent,
    setLastAnchorPoint,
    setOverwriteContent,
    setUploads,
    setUploadsInProgress,
    setWarning,
    uploads,
    uploadsInProgress,
    warning,
    ...passAlong
  };

  return <RichTextareaContext.Provider value={value}>{children}</RichTextareaContext.Provider>;
};

export default RichTextareaProvider;
